import type {
  IvyLeagueChild,
  IvyLeagueChildBreakdown,
  IvyLeagueFamilyBreakdown,
  IvyLeagueFamilyInfo,
  IvyLeagueGrade,
  IvyLeagueProgramKey,
  IvyLeagueAdminConfiguration,
  Kid,
  KidsProgram,
  PricingConfig
} from '../../types/ivyLeague'

const roundCurrency = (amount: number): number => {
  // Display wants cents (e.g. .50), so keep 2-decimal rounding.
  // Use a small epsilon to reduce floating-point edge cases.
  return Math.round((amount + Number.EPSILON) * 100) / 100
}

const roundDollars = (amount: number): number => {
  return Math.round(amount)
}

const allocateReferralPerChild = (
  items: Array<{ childId: string; onCampusTuition: number; offCampusTuition: number }>,
  perChildReferralAmount: number
): {
  totalByChildId: Record<string, number>
  onCampusByChildId: Record<string, number>
} => {
  const totalByChildId: Record<string, number> = {}
  const onCampusByChildId: Record<string, number> = {}
  const perChild = Math.max(0, roundDollars(perChildReferralAmount))
  if (!perChild) return { totalByChildId, onCampusByChildId }

  for (const item of items) {
    const onCampus = Math.max(0, item.onCampusTuition)
    const offCampus = Math.max(0, item.offCampusTuition)

    const appliedOnCampus = Math.min(perChild, Math.floor(onCampus))
    const remaining = Math.max(0, perChild - appliedOnCampus)
    const appliedOffCampus = Math.min(remaining, Math.floor(offCampus))
    const totalApplied = roundDollars(appliedOnCampus + appliedOffCampus)

    if (totalApplied > 0) totalByChildId[item.childId] = totalApplied
    if (appliedOnCampus > 0) onCampusByChildId[item.childId] = roundDollars(appliedOnCampus)
  }

  return { totalByChildId, onCampusByChildId }
}

const inferDaysPerWeekFromProgramName = (programName: string): 3 | 4 | 5 | null => {
  const n = (programName ?? '').toLowerCase()
  if (n.includes('mwf')) return 3
  if (n.includes('3 day')) return 3
  if (n.includes('4 day')) return 4
  if (n.includes('5 day')) return 5
  if (n.includes('full day')) return 5
  return null
}

const isPostGradGradeLabel = (grade: string): boolean => {
  const g = (grade ?? '').trim().toLowerCase()
  if (g === '9' || g === '10') return true
  if (g.includes('grade 9') || g.includes('grade 10')) return true
  // Back-compat with legacy short labels.
  if (g === 'post grads' || g.includes('post grad')) return true
  return false
}

const getDefaultAdminDiscountsAndFees = (admin?: IvyLeagueAdminConfiguration) => {
  // Support both:
  // 1) typed shape: { discounts: { teacherRate, alumniRate, ... }, fees: {...} }
  // 2) sheet-like shape: { discounts: { Teacher: { value }, Alumni: { value }, Referral: { value }, ... } }
  const sheetDiscounts = (admin as any)?.discounts
  const addons = (admin as any)?.addonsAndDiscounts

  const addonsTransportationByDaysPerWeek: Record<number, number> | undefined = addons?.transportation
    ? {
        3: typeof addons.transportation?.['3_day_week'] === 'number' ? addons.transportation['3_day_week'] : 45,
        4: typeof addons.transportation?.['4_day_week'] === 'number' ? addons.transportation['4_day_week'] : 60,
        5: typeof addons.transportation?.['5_day_week'] === 'number' ? addons.transportation['5_day_week'] : 75
      }
    : undefined

  const addonsSiblingByRank: Record<number, number> | undefined = addons?.sibling
    ? Object.keys(addons.sibling).reduce<Record<number, number>>((acc, k) => {
        const n = Number(k)
        const v = (addons.sibling as any)[k]
        if (!Number.isNaN(n) && typeof v === 'number') acc[n] = v
        return acc
      }, {})
    : undefined

  const addonsPaymentRateByType: Record<string, number> | undefined = addons?.payInFull
    ? {
        AUGUST: addons.payInFull.august ?? 0,
        OCTOBER: addons.payInFull.october ?? 0,
        DECEMBER: addons.payInFull.december ?? 0,
        FEBRUARY: addons.payInFull.february ?? 0,
        MONTHLY: addons.payInFull.monthly ?? 0,
        INCENTIVE_CURRENT_STUDENT: addons.payInFull.incentive ?? 0,
        NONE: 0
      }
    : undefined

  const reduceFiveDayToFourDayRate =
    (typeof addons?.adjustments?.reduce_5_to_4_day === 'number' ? addons.adjustments.reduce_5_to_4_day : undefined) ??
    0.09

  const teacherRate =
    admin?.discounts?.teacherRate ??
    (typeof addons?.discounts?.teacher === 'number' ? addons.discounts.teacher : undefined) ??
    (typeof sheetDiscounts?.Teacher?.value === 'number' ? sheetDiscounts.Teacher.value : undefined) ??
    0.15

  const alumniRate =
    admin?.discounts?.alumniRate ??
    (typeof addons?.discounts?.alumni === 'number' ? addons.discounts.alumni : undefined) ??
    (typeof sheetDiscounts?.Alumni?.value === 'number' ? sheetDiscounts.Alumni.value : undefined) ??
    0.05

  const siblingByRank = admin?.discounts?.siblingByRank ?? addonsSiblingByRank ?? { 1: 0, 2: 0.05, 3: 0.15, 4: 0.5, 5: 0.75 }

  const paymentRateByType = admin?.discounts?.paymentRateByType ?? addonsPaymentRateByType ?? {
    AUGUST: 0.09,
    OCTOBER: 0.07,
    DECEMBER: 0.05,
    FEBRUARY: 0.03,
    MONTHLY: 0.03,
    INCENTIVE_CURRENT_STUDENT: 0.05,
    NONE: 0
  }

  const referralAmount =
    admin?.discounts?.referralAmount ??
    (typeof addons?.discounts?.referral === 'number' ? addons.discounts.referral : undefined) ??
    (typeof sheetDiscounts?.Referral?.value === 'number' ? sheetDiscounts.Referral.value : undefined) ??
    500

  const occupationalRateByType = admin?.discounts?.occupationalRateByType ?? {
    Military: (typeof addons?.discounts?.military === 'number' ? addons.discounts.military : 0.1),
    None: 0
  }

  // This value represents the per-week transportation delta for a given days-per-week.
  // - Non-teacher + Parent => discount (subtract)
  const transportationDiscountByDaysPerWeek =
    admin?.discounts?.transportationDiscountByDaysPerWeek ??
    addonsTransportationByDaysPerWeek ??
    {
      3: 45,
      4: 60,
      5: 75
    }

  const sheetFees = (admin as any)?.fees
  const busFeeByDaysPerWeek = admin?.fees?.busFeeByDaysPerWeek ?? sheetFees?.busFeeByDaysPerWeek ?? { 3: 360, 4: 600, 5: 600 }
  const finaleTripFee =
    admin?.fees?.finaleTripFee ??
    (typeof addons?.fees?.post_grad === 'number' ? addons.fees.post_grad : undefined) ??
    sheetFees?.finaleTripFee ??
    800
  const creditCardSurchargeRate =
    admin?.fees?.creditCardSurchargeRate ??
    (typeof addons?.fees?.credit_card === 'number' ? addons.fees.credit_card : undefined) ??
    sheetFees?.creditCardSurchargeRate ??
    0.035

  return {
    teacherRate,
    alumniRate,
    siblingByRank,
    paymentRateByType,
    referralAmount,
    occupationalRateByType,
    transportationDiscountByDaysPerWeek,
    reduceFiveDayToFourDayRate,
    busFeeByDaysPerWeek,
    finaleTripFee,
    creditCardSurchargeRate
  }
}

const getSiblingRateForRank = (siblingByRank: Record<number, number>, rank: number): number => {
  if (rank in siblingByRank) return siblingByRank[rank] ?? 0

  // Defensive defaults if config is partial.
  if (rank === 1) return 0
  if (rank === 2) return siblingByRank[2] ?? 0.05
  if (rank === 3) return siblingByRank[3] ?? 0.15

  // If there are >5 kids, apply the least-expensive (5th) rate to 5th+ when provided.
  if (rank >= 5) return siblingByRank[5] ?? siblingByRank[4] ?? siblingByRank[3] ?? 0

  // Rank 4 fallback.
  return siblingByRank[4] ?? siblingByRank[3] ?? 0
}

export const getIvyLeagueCreditCardSurchargeRate = (adminConfiguration?: IvyLeagueAdminConfiguration): number => {
  return getDefaultAdminDiscountsAndFees(adminConfiguration).creditCardSurchargeRate
}

export const IVY_LEAGUE_HYBRID_CONSTRAINTS = {
  eligibleGradeMin: 7,
  eligibleGradeMax: 10,
  minOffCampusWeeks: 2,
  minOnCampusWeeks: 1,
  minTotalWeeks: 3,
  maxTotalWeeks: 8
} as const

export const parseIvyLeagueGradeNumber = (grade: string): number | null => {
  const m = String(grade ?? '').match(/\d+/)
  if (!m) return null
  const n = parseInt(m[0], 10)
  return Number.isFinite(n) ? n : null
}

export const isIvyLeagueHybridEligibleGrade = (grade: string): boolean => {
  const gradeNum = parseIvyLeagueGradeNumber(grade)
  if (gradeNum == null) return false
  return gradeNum >= IVY_LEAGUE_HYBRID_CONSTRAINTS.eligibleGradeMin && gradeNum <= IVY_LEAGUE_HYBRID_CONSTRAINTS.eligibleGradeMax
}

export const getIvyLeagueHybridValidationErrors = (grade: string, programs: KidsProgram[]): string[] => {
  if (!isIvyLeagueHybridEligibleGrade(grade)) return []

  const weeksOn = (programs ?? [])
    .filter((p) => p.programType === 'OnCampus')
    .reduce((s, p) => s + (p.weekCount ?? 0), 0)
  const weeksOff = (programs ?? [])
    .filter((p) => p.programType === 'OffCampus')
    .reduce((s, p) => s + (p.weekCount ?? 0), 0)

  const isHybrid = weeksOn > 0 && weeksOff > 0
  if (!isHybrid) return []

  const total = weeksOn + weeksOff
  const errors: string[] = []

  if (weeksOff < IVY_LEAGUE_HYBRID_CONSTRAINTS.minOffCampusWeeks) errors.push('hybrid needs 2+ off-campus weeks')
  if (weeksOn < IVY_LEAGUE_HYBRID_CONSTRAINTS.minOnCampusWeeks) errors.push('hybrid needs 1+ on-campus weeks')
  if (total < IVY_LEAGUE_HYBRID_CONSTRAINTS.minTotalWeeks) errors.push('hybrid min is 3 total weeks')
  if (total > IVY_LEAGUE_HYBRID_CONSTRAINTS.maxTotalWeeks) errors.push('hybrid max is 8 total weeks')

  return errors
}

export function calculateIvyLeagueTotalsFromAdminConfiguration (
  kids: Kid[],
  familyInfo: IvyLeagueFamilyInfo,
  adminConfiguration: IvyLeagueAdminConfiguration | undefined
): { childBreakdowns: IvyLeagueChildBreakdown[]; familyBreakdown: IvyLeagueFamilyBreakdown } {
  const pricingByGrade = adminConfiguration?.programPricing
  if (!pricingByGrade) {
    return {
      childBreakdowns: [],
      familyBreakdown: {
        familyGross: 0,
        familySubtotalBeforeReferral: 0,
        referralDiscountAmount: 0,
        familySubtotalAfterReferral: 0,
        creditCardFee: 0,
        finalAmountDue: 0
      }
    }
  }

  const cfg = getDefaultAdminDiscountsAndFees(adminConfiguration)

  // Referral discount is per-camper.
  const referralPerChildAmount = familyInfo.hasReferralDiscount ? cfg.referralAmount : 0

  // Phase 1: compute gross tuition and pre-% balance per kid.
  // (We do an initial pass to get per-kid onCampusTuition so we can allocate referral.)
  const basePerKid = (kids ?? []).map((kid) => {
    const gradePricing = kid.grade ? pricingByGrade[kid.grade] : undefined
    const programs = kid.programs ?? []

    const totalWeeks = programs.reduce((s, p) => s + (p.weekCount ?? 0), 0)
    const weeksOnCampus = programs
      .filter((p) => p.programType === 'OnCampus')
      .reduce((s, p) => s + (p.weekCount ?? 0), 0)

    // Break out on/off-campus components for discounts and add-ons.
    const onCampusTuitionRaw = programs
      .filter((p) => p.programType === 'OnCampus')
      .reduce((s, p) => {
        const base =
          gradePricing?.[p.programType]?.[p.programName]?.[totalWeeks] ??
          gradePricing?.[p.programType]?.[p.programName]?.[String(totalWeeks)]
        if (!totalWeeks || !p.programType || !p.programName || base == null) return s
        return s + (Number(base) / totalWeeks) * (p.weekCount ?? 0)
      }, 0)

    const offCampusTuition = programs
      .filter((p) => p.programType === 'OffCampus')
      .reduce((s, p) => {
        const base =
          gradePricing?.[p.programType]?.[p.programName]?.[totalWeeks] ??
          gradePricing?.[p.programType]?.[p.programName]?.[String(totalWeeks)]
        if (!totalWeeks || !p.programType || !p.programName || base == null) return s
        return s + (Number(base) / totalWeeks) * (p.weekCount ?? 0)
      }, 0)

    // 5-day -> 4-day adjustment (aka "reduce_5_to_4_day"): apply ONLY to on-campus 5-day tuition.
    // This occurs before teacher/other discounts and also changes the transportation day-rate from 5-day to 4-day.
    const wantsFourDayFromFiveDay = Boolean(kid.usesExtendedHours)
    const onCampusFiveDayTuitionRaw = wantsFourDayFromFiveDay
      ? programs
          .filter((p) => p.programType === 'OnCampus')
          .reduce((s, p) => {
            const days = inferDaysPerWeekFromProgramName(p.programName)
            if (days !== 5) return s
            const base =
              gradePricing?.[p.programType]?.[p.programName]?.[totalWeeks] ??
              gradePricing?.[p.programType]?.[p.programName]?.[String(totalWeeks)]
            if (!totalWeeks || !p.programType || !p.programName || base == null) return s
            return s + (Number(base) / totalWeeks) * (p.weekCount ?? 0)
          }, 0)
      : 0

    const fourDayDiscount = onCampusFiveDayTuitionRaw > 0
      ? roundCurrency(cfg.reduceFiveDayToFourDayRate * onCampusFiveDayTuitionRaw)
      : 0

    const onCampusTuition = Math.max(0, onCampusTuitionRaw - fourDayDiscount)
    const grossTuition = onCampusTuition + offCampusTuition

    const inferredDays =
      programs
        .map((p) => inferDaysPerWeekFromProgramName(p.programName))
        .find((v) => v != null) ?? null

    const isPostGrad = isPostGradGradeLabel(kid.grade)
    const isToddlers = (kid.grade ?? '').toLowerCase().includes('2 years old')

    return {
      childId: kid.id,
      onCampusTuitionRaw,
      fourDayDiscount,
      onCampusTuition,
      offCampusTuition,
      grossTuition,
      teacherDiscount: 0,
      occupationalDiscount: 0,
      transportationDiscount: 0,
      tuitionBalance: 0,
      siblingDiscount: 0,
      alumniDiscount: 0,
      paymentDiscount: 0,
      busFee: 0,
      finaleFee: 0,
      childSubtotal: 0,
      _meta: {
        totalWeeks,
        weeksOnCampus,
        inferredDays,
        isPostGrad,
        isToddlers,
        wantsFourDayFromFiveDay,
        transportMode: (kid.transportMode ?? 'Parent') as 'Bus' | 'Parent'
      }
    }
  })

  const { totalByChildId: referralAllocationByChildId, onCampusByChildId: referralOnCampusByChildId } =
    allocateReferralPerChild(
      basePerKid.map((b) => ({
        childId: b.childId,
        onCampusTuition: b.onCampusTuition,
        offCampusTuition: b.offCampusTuition
      })),
      referralPerChildAmount
    )

  const referralDiscountAmount = Object.values(referralAllocationByChildId).reduce((s, v) => s + (v ?? 0), 0)

  const discountableBalanceByChildId: Record<string, number> = {}

  // Phase 1: Referral is considered first and reduces the base used for subsequent discounts.
  // To preserve the existing family-level referral subtraction, we do NOT subtract referral from
  // per-child subtotals; we only reduce the discount bases.
  const prePct: IvyLeagueChildBreakdown[] = basePerKid.map((b: any) => {
    const meta = b._meta as {
      totalWeeks: number
      weeksOnCampus: number
      inferredDays: 3 | 4 | 5 | null
      isPostGrad: boolean
      isToddlers: boolean
      wantsFourDayFromFiveDay: boolean
      transportMode: 'Bus' | 'Parent'
    }

    const referralApplied = referralAllocationByChildId[b.childId] ?? 0
    const tuitionBaseAfterReferral = Math.max(0, b.grossTuition - referralApplied)

    // Transportation discount (applies ONLY on OnCampus weeks):
    // Priority rule: for 3-week campers, tuition does not include bus,
    // so we do not apply parent transportation discount.
    // Case 1: Not teacher + Bus => bus already baked into tuition => no discount
    // Case 2: Not teacher + Parent => discount per on-campus week
    // Case 3: Teacher + Parent => no discount (teacher discount already applied)
    // Case 4: Teacher + Bus => handled later as an add-on fee (not a discount)
    // Note: Toddlers do not receive the transportation discount (Case 2).
    let transportationDiscount = 0
    const isThreeWeekCamper = meta.totalWeeks === 3
    const transportationDays = (meta.wantsFourDayFromFiveDay && meta.inferredDays === 5) ? 4 : meta.inferredDays
    if (
      meta.weeksOnCampus > 0 &&
      transportationDays != null &&
      !familyInfo.isTeacherFamily &&
      meta.transportMode === 'Parent' &&
      !isThreeWeekCamper &&
      !meta.isToddlers
    ) {
      const perWeek = cfg.transportationDiscountByDaysPerWeek[transportationDays] ?? 0
      transportationDiscount = roundCurrency(perWeek * meta.weeksOnCampus)
    }

    // "Not using transportation" discount is applied next.
    // Referral reduces the base used for subsequent discounts, but referral itself is applied
    // at the family level later (consistent with existing outputs).
    const tuitionBalance = Math.max(0, b.grossTuition - transportationDiscount)
    const discountableBalance = Math.max(0, tuitionBaseAfterReferral - transportationDiscount)
    discountableBalanceByChildId[b.childId] = discountableBalance

    const { _meta, ...rest } = b
    return {
      ...(rest as IvyLeagueChildBreakdown),
      referralDiscount: referralApplied,
      transportationDiscount,
      tuitionBalance
    }
  })

  // Phase 2: Alumni discount
  const withAlumni = prePct.map((b) => {
    if (!familyInfo.isAlumniFamily) return b
    const base = discountableBalanceByChildId[b.childId] ?? 0
    const alumniDiscount = roundCurrency(cfg.alumniRate * base)
    return {
      ...b,
      alumniDiscount
    }
  })

  // Phase 3: Sibling discount (ranked by balance after alumni)
  const ranked = [...withAlumni].sort((a, b) => {
    const baseA = Math.max(0, (discountableBalanceByChildId[a.childId] ?? 0) - (a.alumniDiscount ?? 0))
    const baseB = Math.max(0, (discountableBalanceByChildId[b.childId] ?? 0) - (b.alumniDiscount ?? 0))
    return baseB - baseA
  })

  for (let i = 0; i < ranked.length; i++) {
    const rank = i + 1
    const baseForSibling = Math.max(0, (discountableBalanceByChildId[ranked[i].childId] ?? 0) - (ranked[i].alumniDiscount ?? 0))
    const rate = getSiblingRateForRank(cfg.siblingByRank, rank)
    ranked[i].siblingDiscount = roundCurrency(rate * baseForSibling)
  }

  // Phase 4: Teacher/Military discount
  for (let i = 0; i < ranked.length; i++) {
    const kidBase = basePerKid.find((x) => x.childId === ranked[i].childId)
    const referralAppliedToOnCampus = referralOnCampusByChildId[ranked[i].childId] ?? 0
    const onCampusAfterReferral = kidBase
      ? Math.max(0, (kidBase as any).onCampusTuition - referralAppliedToOnCampus)
      : 0

    // Teacher discount is applied after earlier discounts in the priority order (alumni/sibling).
    // Since teacher discount is on-campus-only, we clamp the earlier discounts to the on-campus base.
    const preTeacherDiscounts = Math.max(0, (ranked[i].alumniDiscount ?? 0) + (ranked[i].siblingDiscount ?? 0))
    const teacherBaseOnCampus = Math.max(0, onCampusAfterReferral - Math.min(onCampusAfterReferral, preTeacherDiscounts))

    ranked[i].teacherDiscount = familyInfo.isTeacherFamily
      ? roundCurrency(
        familyInfo.teacherDiscountOverride ??
        cfg.teacherRate * teacherBaseOnCampus
      )
      : 0

    const occupationalRate = !familyInfo.isTeacherFamily
      ? (cfg.occupationalRateByType?.[familyInfo.occupationalDiscountType] ?? 0)
      : 0

    const baseForOcc = Math.max(
      0,
      (discountableBalanceByChildId[ranked[i].childId] ?? 0) - (ranked[i].alumniDiscount ?? 0) - (ranked[i].siblingDiscount ?? 0)
    )
    ranked[i].occupationalDiscount = occupationalRate > 0
      ? roundCurrency(occupationalRate * baseForOcc)
      : 0
  }

  // Phase 5: Pay-in-full discount
  const paymentRate = cfg.paymentRateByType?.[familyInfo.paymentDiscountType] ?? 0
  for (let i = 0; i < ranked.length; i++) {
    const baseForPayment = Math.max(
      0,
      (discountableBalanceByChildId[ranked[i].childId] ?? 0) -
        (ranked[i].alumniDiscount ?? 0) -
        (ranked[i].siblingDiscount ?? 0) -
        (ranked[i].teacherDiscount ?? 0) -
        (ranked[i].occupationalDiscount ?? 0)
    )
    ranked[i].paymentDiscount = roundCurrency(paymentRate * baseForPayment)
  }

  // Phase 6: add-ons (bus + finale) and subtotal
  const childBreakdowns = (kids ?? []).map((kid) => {
    const b = ranked.find((x) => x.childId === kid.id)
    if (!b) {
      return {
        childId: kid.id,
        onCampusTuitionRaw: 0,
        fourDayDiscount: 0,
        onCampusTuition: 0,
        offCampusTuition: 0,
        grossTuition: 0,
        referralDiscount: 0,
        teacherDiscount: 0,
        occupationalDiscount: 0,
        transportationDiscount: 0,
        tuitionBalance: 0,
        siblingDiscount: 0,
        alumniDiscount: 0,
        paymentDiscount: 0,
        busFee: 0,
        finaleFee: 0,
        childSubtotal: 0
      }
    }

    // Bus fee:
    // - Non-teacher + post-grad (9/10) + Bus + on-campus participation => flat $600
    // - Non-teacher + 3-week camper + Bus + on-campus participation => flat $600
    // - Teacher + Bus + on-campus participation => flat $360 for 3-day, flat $600 for 4/5-day
    let busFee = 0
    if ((kid.transportMode ?? 'Parent') === 'Bus') {
      const programs = kid.programs ?? []
      const totalWeeks = programs.reduce((s, p) => s + (p.weekCount ?? 0), 0)
      const weeksOnCampus = programs
        .filter((p) => p.programType === 'OnCampus')
        .reduce((s, p) => s + (p.weekCount ?? 0), 0)
      const isPostGrad = isPostGradGradeLabel(kid.grade)

      if (!familyInfo.isTeacherFamily && weeksOnCampus > 0 && (isPostGrad || totalWeeks === 3)) {
        busFee = 600
      } else if (familyInfo.isTeacherFamily && weeksOnCampus > 0) {
        const inferredDays =
          programs
            .map((p) => inferDaysPerWeekFromProgramName(p.programName))
            .find((v) => v != null) ?? null

        const transportationDays = (Boolean(kid.usesExtendedHours) && inferredDays === 5) ? 4 : inferredDays
        const days = transportationDays ?? inferredDays

        if (days === 3) {
          busFee = cfg.busFeeByDaysPerWeek[3] ?? 360
        } else {
          busFee = cfg.busFeeByDaysPerWeek[days ?? 5] ?? 600
        }
      }
    }

    // Table note: "Post Grad Surcharge" for post grads attending final trip.
    // Keep other grades at $0 even if selected.
    const finaleFeeAdjusted = (kid.finaleTrip && isPostGradGradeLabel(kid.grade)) ? cfg.finaleTripFee : 0

    const afterPct = Math.max(
      0,
      b.tuitionBalance -
        (b.alumniDiscount ?? 0) -
        (b.siblingDiscount ?? 0) -
        (b.teacherDiscount ?? 0) -
        (b.occupationalDiscount ?? 0) -
        (b.paymentDiscount ?? 0)
    )
    const childSubtotal = afterPct + busFee + finaleFeeAdjusted

    return {
      ...b,
      busFee,
      finaleFee: finaleFeeAdjusted,
      childSubtotal
    }
  })

  const familySubtotalBeforeReferral = childBreakdowns.reduce((s, c) => s + c.childSubtotal, 0)
  const familySubtotalAfterReferral = Math.max(0, familySubtotalBeforeReferral - referralDiscountAmount)
  const creditCardFee = familyInfo.paymentMethod === 'CreditCard'
    ? (cfg.creditCardSurchargeRate * familySubtotalAfterReferral)
    : 0
  const finalAmountDue = roundCurrency(familySubtotalAfterReferral + creditCardFee)
  const familyGross = childBreakdowns.reduce((s, c) => s + c.grossTuition, 0)

  return {
    childBreakdowns,
    familyBreakdown: {
      familyGross,
      familySubtotalBeforeReferral,
      referralDiscountAmount,
      familySubtotalAfterReferral,
      creditCardFee,
      finalAmountDue
    }
  }
}

/**
 * Calculate tuition and fees per Ivy League rules.
 * Order: base tuition → four-day adj → referral (reduces later discount bases)
 * → not using transportation → alumni → sibling → teacher/military → pay in full
 * → + transportation (teacher) → bus/finale → referral (family-level) → credit card → round.
 */
export function calculateIvyLeagueTotals (
  children: IvyLeagueChild[],
  familyInfo: IvyLeagueFamilyInfo,
  pricingConfig: PricingConfig | null
): { childBreakdowns: IvyLeagueChildBreakdown[]; familyBreakdown: IvyLeagueFamilyBreakdown } {
  if (!pricingConfig) {
    return {
      childBreakdowns: [],
      familyBreakdown: {
        familyGross: 0,
        familySubtotalBeforeReferral: 0,
        referralDiscountAmount: 0,
        familySubtotalAfterReferral: 0,
        creditCardFee: 0,
        finalAmountDue: 0
      }
    }
  }

  const pc = pricingConfig
  const d = pc.discounts
  const t = pc.tuition
  const f = pc.fees

  const referralPerChildAmount = familyInfo.hasReferralDiscount ? (d.referral?.amount ?? 0) : 0

  // Step 1: Compute per-child base tuition and pre-sibling amounts
  // (We do an initial pass to get onCampusTuition so we can allocate referral.)
  const basePerChild = children.map((child) => {
      const programKey = child.programKey
      const weeksOn = child.weeksOnCampus
      const weeksOff = child.weeksOffCampus
      const weeksTotal = child.weeksTotal

      let onCampusTuitionRaw = 0
      if (weeksOn > 0 && t.package.onCampus[programKey]) {
        onCampusTuitionRaw = t.package.onCampus[programKey][weeksOn] ?? 0
      }

      let offCampusTuition = 0
      if (weeksOff > 0) {
        if (child.fullDayWeeks > 0 || child.postGradWeeks > 0) {
          const fd = t.weekly.fullDayPerWeek[weeksTotal] ?? 0
          const pg = t.weekly.postGradPerWeek[weeksTotal] ?? 0
          offCampusTuition = fd * child.fullDayWeeks + pg * child.postGradWeeks
        } else if (programKey === 'GRADS_7_8' && t.package.offCampus.GRADS_7_8) {
          offCampusTuition = t.package.offCampus.GRADS_7_8[weeksOff] ?? 0
        } else if (programKey === 'POST_GRADS_9_10' && t.package.offCampus.POST_GRADS_9_10) {
          offCampusTuition = t.package.offCampus.POST_GRADS_9_10[weeksOff] ?? 0
        }
      }

      const fourDayRate = d.fourDayFromFiveDay?.rate ?? 0
      const fourDayDiscount = child.isFourDayFromFiveDay
        ? fourDayRate * onCampusTuitionRaw
        : 0
      const onCampusTuition = Math.max(0, onCampusTuitionRaw - fourDayDiscount)
      const grossTuition = onCampusTuition + offCampusTuition

      const transportEligible =
        !familyInfo.isTeacherFamily &&
        child.transportMode === 'Parent' &&
        child.usesExtendedHours &&
        programKey !== 'TODDLERS_MINI_MINI' &&
        weeksOn >= 1
      const transportAmt = transportEligible
        ? (d.transport?.byDaysPerWeek?.[child.daysPerWeek] ?? 0) * weeksOn
        : 0

      return {
        childId: child.childId,
        onCampusTuitionRaw,
        fourDayDiscount,
        onCampusTuition,
        offCampusTuition,
        grossTuition,
        referralDiscount: 0,
        teacherDiscount: 0,
        occupationalDiscount: 0,
        transportationDiscount: transportAmt,
        tuitionBalance: 0,
        siblingDiscount: 0,
        alumniDiscount: 0,
        paymentDiscount: 0,
        busFee: 0,
        finaleFee: 0,
        childSubtotal: 0
      }
    })

  const { totalByChildId: referralAllocationByChildId, onCampusByChildId: referralOnCampusByChildId } =
    allocateReferralPerChild(
      basePerChild.map((b) => ({
        childId: b.childId,
        onCampusTuition: b.onCampusTuition,
        offCampusTuition: b.offCampusTuition
      })),
      referralPerChildAmount
    )

  const referralDiscountAmount = Object.values(referralAllocationByChildId).reduce((s, v) => s + (v ?? 0), 0)

  const discountableBalanceByChildId: Record<string, number> = {}

  const withBalance: IvyLeagueChildBreakdown[] = basePerChild.map((b) => {
    const referralApplied = referralAllocationByChildId[b.childId] ?? 0
    const tuitionBaseAfterReferral = Math.max(0, b.grossTuition - referralApplied)

    const tuitionBalance = Math.max(0, b.grossTuition - (b.transportationDiscount ?? 0))
    const discountableBalance = Math.max(0, tuitionBaseAfterReferral - (b.transportationDiscount ?? 0))
    discountableBalanceByChildId[b.childId] = discountableBalance

    return {
      ...b,
      referralDiscount: referralApplied,
      transportationDiscount: b.transportationDiscount,
      tuitionBalance
    }
  })

  // Alumni then sibling (ranked by balance after alumni)
  const withAlumni = withBalance.map((b) => {
    if (!familyInfo.isAlumniFamily) return b
    const base = discountableBalanceByChildId[b.childId] ?? 0
    const alumniDiscount = (d.alumni?.rate ?? 0) * base
    return { ...b, alumniDiscount }
  })

  const sorted = [...withAlumni].sort((a, b) => {
    const baseA = Math.max(0, (discountableBalanceByChildId[a.childId] ?? 0) - (a.alumniDiscount ?? 0))
    const baseB = Math.max(0, (discountableBalanceByChildId[b.childId] ?? 0) - (b.alumniDiscount ?? 0))
    return baseB - baseA
  })

  for (let i = 0; i < sorted.length; i++) {
    const rank = i + 1
    const rate = d.sibling?.byRank?.[rank] ?? 0
    const baseForSibling = Math.max(0, (discountableBalanceByChildId[sorted[i].childId] ?? 0) - (sorted[i].alumniDiscount ?? 0))
    sorted[i].siblingDiscount = rate * baseForSibling
  }

  // Teacher/Military then Pay-in-full
  const teacherRate = d.teacher?.rate ?? 0
  const paymentRate = d.payment?.byType?.[familyInfo.paymentDiscountType] ?? 0

  for (let i = 0; i < sorted.length; i++) {
    const referralAppliedToOnCampus = referralOnCampusByChildId[sorted[i].childId] ?? 0
    const onCampusAfterReferral = Math.max(0, sorted[i].onCampusTuition - referralAppliedToOnCampus)

    const preTeacherDiscounts = Math.max(0, (sorted[i].alumniDiscount ?? 0) + (sorted[i].siblingDiscount ?? 0))
    const teacherBaseOnCampus = Math.max(0, onCampusAfterReferral - Math.min(onCampusAfterReferral, preTeacherDiscounts))

    sorted[i].teacherDiscount = familyInfo.isTeacherFamily
      ? (familyInfo.teacherDiscountOverride ?? teacherRate * teacherBaseOnCampus)
      : 0

    const occupationalRate = !familyInfo.isTeacherFamily && familyInfo.occupationalDiscountType !== 'None'
      ? (d.occupational?.[familyInfo.occupationalDiscountType] ?? 0)
      : 0

    const baseForOcc = Math.max(
      0,
      (discountableBalanceByChildId[sorted[i].childId] ?? 0) - (sorted[i].alumniDiscount ?? 0) - (sorted[i].siblingDiscount ?? 0)
    )
    sorted[i].occupationalDiscount = occupationalRate > 0 ? occupationalRate * baseForOcc : 0

    const baseForPayment = Math.max(
      0,
      (discountableBalanceByChildId[sorted[i].childId] ?? 0) -
        (sorted[i].alumniDiscount ?? 0) -
        (sorted[i].siblingDiscount ?? 0) -
        (sorted[i].teacherDiscount ?? 0) -
        (sorted[i].occupationalDiscount ?? 0)
    )
    sorted[i].paymentDiscount = paymentRate * baseForPayment
  }

  const childBreakdowns: IvyLeagueChildBreakdown[] = children.map((child) => {
    const b = sorted.find((x) => x.childId === child.childId)!
    const afterPct =
      b.tuitionBalance -
      (b.alumniDiscount ?? 0) -
      (b.siblingDiscount ?? 0) -
      (b.teacherDiscount ?? 0) -
      (b.occupationalDiscount ?? 0) -
      (b.paymentDiscount ?? 0)
    const childTuitionAfterPct = Math.max(0, afterPct)

    let busFee = 0
    if (child.transportMode === 'Bus' && child.weeksOnCampus > 0) {
      if (familyInfo.isTeacherFamily) {
        busFee = child.daysPerWeek === 3
          ? (f.bus?.byDaysPerWeek?.[3] ?? 360)
          : (f.bus?.byDaysPerWeek?.[child.daysPerWeek] ?? 600)
      } else {
        const base = f.bus?.byDaysPerWeek?.[child.daysPerWeek] ?? (child.daysPerWeek === 3 ? 360 : 600)
        busFee = base * (child.weeksOnCampus / child.weeksTotal)
      }
    }

    const finaleFee = child.finaleTrip ? (f.finaleTrip ?? 0) : 0
    const childSubtotal = childTuitionAfterPct + busFee + finaleFee

    return {
      ...b,
      busFee,
      finaleFee,
      childSubtotal
    }
  })

  const familySubtotalBeforeReferral = childBreakdowns.reduce((s, c) => s + c.childSubtotal, 0)
  const referralAmount = referralDiscountAmount
  const familySubtotalAfterReferral = Math.max(
    0,
    familySubtotalBeforeReferral - referralAmount
  )
  const creditCardFee =
    familyInfo.paymentMethod === 'CreditCard'
      ? (f.creditCardSurchargeRate ?? 0) * familySubtotalAfterReferral
      : 0
  const finalAmountDue = roundCurrency(familySubtotalAfterReferral + creditCardFee)

  const familyGross = childBreakdowns.reduce((s, c) => s + c.grossTuition, 0)

  return {
    childBreakdowns,
    familyBreakdown: {
      familyGross,
      familySubtotalBeforeReferral,
      referralDiscountAmount: referralAmount,
      familySubtotalAfterReferral,
      creditCardFee,
      finalAmountDue
    }
  }
}

/**
 * Derives programKey from grade and daysPerWeek.
 * Grades 9-10 use POST_GRADS_9_10 (hybrid weeks handled separately).
 */
export const getProgramKeyFromGrade = (
  grade: string,
  daysPerWeek?: 3 | 4 | 5
): IvyLeagueProgramKey => {
  if (grade === '2yo') return 'TODDLERS_MINI_MINI'
  if (grade === '3yo' || grade === '4yo') return 'PREK_5DAY_9_415'
  if (grade === 'K') return (daysPerWeek === 3 ? 'K_3DAY' : 'K_5DAY')
  const num = parseInt(grade, 10)
  if (num >= 1 && num <= 6) return 'FULL_DAY_1_10'
  if (num === 7 || num === 8) return 'GRADS_7_8'
  if (num === 9 || num === 10) return 'POST_GRADS_9_10'
  return 'FULL_DAY_1_10'
}

export const formatNumberWithCommas = (number: number): string => {
  if (typeof number !== 'number') number = parseFloat(String(number))
  if (isNaN(number)) return 'Invalid input'
  return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const IVY_LEAGUE_GRADES: IvyLeagueGrade[] = [
  '2yo', '3yo', '4yo', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'
]

export const isPostGrad = (grade: string): boolean => {
  const n = parseInt(grade, 10)
  return grade === '9' || grade === '10' || (!isNaN(n) && n >= 9)
}
